import type { LabelValue } from "newui/types/LabelValue.type";

import {
	getCompanyProfileTypes,
	getDocumentCategories,
	getProcurementMethods,
	getSectors,
	getUnitMeasures,
} from "./api";
import type { CommonLookupResponse } from "./tanstack-base";
import { useCommonLookup } from "./tanstack-base";
import { SectorOption } from "newui/types/SectorOption.type";
import { getClarificationTypes } from "newui/features/Rfx/Clarifications/api/clarification-api";

interface DocumentCategoryLabelValue extends LabelValue<number> {
	exclude_profile_type: number | null;
	parent_id: number | null;
}

type CategoryDisplayOptions = "categories" | "sub-categories" | "both";

const fetchCompanyProfileTypes = async (): Promise<LabelValue<number>[]> => {
	const raw = await getCompanyProfileTypes();
	return (
		raw?.map((profile) => ({ label: profile.name, value: profile.id })) ?? []
	);
};

const fetchDocumentCategories = async (
	excludingProfileTypeId: number | null
): Promise<DocumentCategoryLabelValue[]> => {
	const raw = await getDocumentCategories(excludingProfileTypeId);
	return raw?.map((dc) => ({
		label: dc.name,
		value: dc.id,
		exclude_from_profile_id: dc.exclude_from_profile_id,
		parent_id: dc.parent_id,
	}));
};

const fetchSectors = async (byCompanyId?: number): Promise<SectorOption[]> => {
	const raw = await getSectors(byCompanyId);
	return (
		raw?.map(
			(pm) =>
				({
					id: pm.id,
					name: pm.name,
					projects_count: pm.projects_count,
					label: pm.name,
					value: pm.id,
				}) as SectorOption
		) ?? []
	);
};

const fetchProcurementMethods = async (): Promise<LabelValue<number>[]> => {
	const raw = await getProcurementMethods();
	return raw?.map((pm) => ({ label: pm.name, value: pm.id })) ?? [];
};

const fetchUnitMeasures = async (): Promise<LabelValue<number>[]> => {
	const raw = await getUnitMeasures();
	return raw?.map((um) => ({ label: um.name, value: um.id })) ?? [];
};

/**
 * Retrieves Document Categories from database and then caches locally
 * @returns
 */
export const useDocumentCategories = (
	excludeProfileType: number | null = null,
	dataReturned: CategoryDisplayOptions | null = null
): CommonLookupResponse<DocumentCategoryLabelValue> => {
	const data = useCommonLookup<DocumentCategoryLabelValue>(
		`documentCategories-${excludeProfileType ?? "all"}`,
		fetchDocumentCategories
	);

	const filterData = (
		valuesToFilter: DocumentCategoryLabelValue[],
		filterByCategory: boolean
	) => {
		return valuesToFilter.filter((category) =>
			filterByCategory ? category.parent_id === 0 : category.parent_id !== 0
		);
	};

	const dataToReturn = {
		...data,
		values:
			(dataReturned ?? "both") === "both"
				? data.values
				: filterData(data.values, dataReturned === "categories"),
	};

	return excludeProfileType === null
		? dataToReturn
		: {
				...dataToReturn,
				values: dataToReturn.values.filter(
					(documentCategory) =>
						documentCategory.exclude_profile_type !== excludeProfileType
				),
			};
};

/**
 * Retrieves Procurement Methods from database and then caches locally
 * @returns
 */
export const useCompanyProfileTypes = (): CommonLookupResponse<
	LabelValue<number>
> =>
	useCommonLookup<LabelValue<number>>(
		"procurementMethods",
		fetchCompanyProfileTypes
	);

/**
 * Retrieves Procurement Methods from database and then caches locally
 * @returns
 */
export const useProcurementMethods = (): CommonLookupResponse<
	LabelValue<number>
> =>
	useCommonLookup<LabelValue<number>>(
		"procurementMethods",
		fetchProcurementMethods
	);

/**
 * Retrieves all Sectors from database and then caches locally
 * @returns
 */
export const useSectors = (
	byCompanyId?: number
): CommonLookupResponse<SectorOption> => {
	if (byCompanyId) {
		return useCommonLookup<SectorOption, number>(
			`sectors-${byCompanyId}`,
			fetchSectors,
			byCompanyId,
			5
		);
	}

	return useCommonLookup<SectorOption>("sectors-all", fetchSectors);
};

/**
 * Retrieves Units and Measures from database and then caches locally
 * @returns
 */
export const useUnitsAndMeasures = (): CommonLookupResponse<
	LabelValue<number>
> => useCommonLookup<LabelValue<number>>("unitMeasures", fetchUnitMeasures);

const fetchClarificationTypes = async (): Promise<LabelValue<number>[]> => {
	const raw = await getClarificationTypes();
	return raw?.map((ct) => ({ label: ct.name, value: ct.id })) ?? [];
};

/**
 * Retrieves Clarification Types from database and then caches locally.
 * Shared between the RFx Clarifications module and the panel-member
 * clarification form on the Evaluation flow.
 */
export const useClarificationTypes = (): CommonLookupResponse<
	LabelValue<number>
> =>
	useCommonLookup<LabelValue<number>>(
		"clarificationTypes",
		fetchClarificationTypes
	);
